import React from 'react';
import { useTranslation } from 'react-i18next';
import { Blueprint } from 'types/rest/assetCatalogTypes';
import { blueprintAvatarColor } from '../../../store/blueprints/blueprintsUtil';
import BlazeDataIcon from '../../../components/icons/BlazeDataIcon';
import styles from './BlueprintCard.module.css';
import BlueprintTag from './BlueprintTag';

interface Props {
  blueprint: Blueprint;
  onOpen: (id: string) => void;
  onAddToCatalog: (id: string) => void;
}

const BlueprintCard = ({ blueprint, onOpen, onAddToCatalog }: Props) => {
  const { t } = useTranslation('blueprints');
  return (
    <div
      className={`card ${styles.card}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(blueprint.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(blueprint.id);
      }}
    >
      <div
        className={styles.thumb}
        style={
          blueprint.thumbnail
            ? undefined
            : {
                background: `linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0)), ${blueprintAvatarColor(
                  blueprint.name,
                )}`,
              }
        }
      >
        {blueprint.thumbnail ? (
          <img src={blueprint.thumbnail} alt="" />
        ) : (
          <span className={styles.placeholder} style={{ color: '#fff' }}>
            {blueprint.name.slice(0, 2).toUpperCase()}
          </span>
        )}
        {blueprint.hasBlazeData && (
          <BlazeDataIcon size={14} title="Includes a BlazeData dataset" className={styles.hasData} />
        )}
      </div>
      <div className={styles.body}>
        <h4 className={styles.name}>{blueprint.name}</h4>
        <div className={styles.tags}>
          {blueprint.tags.map(tag => (
            <BlueprintTag key={tag} tag={tag} />
          ))}
        </div>
        <p className={styles.desc}>{blueprint.description}</p>
        <div className={styles.foot}>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <span className="badge badge-light">
              {t('card.transactions', { count: blueprint.transactionCount ?? blueprint.transactions.length })}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={e => {
              e.stopPropagation();
              onAddToCatalog(blueprint.id);
            }}
          >
            {t('card.add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlueprintCard;
